import { ErrorStrategy } from '@flipt-io/flipt-client-js'
import { FliptProvider, useFliptSelector } from '@flipt-io/flipt-client-react'
import {
  type FliptEnvironment,
  FLIPT_SNAPSHOT_STORAGE_KEY,
  buildFliptClientOptions,
} from '@gozoki/feature-flags'
import { type ReactNode, useEffect } from 'react'

const fliptUrl = import.meta.env.VITE_FLIPT_URL

const mode = import.meta.env.MODE

const fliptEnvironment: FliptEnvironment =
  mode === 'production' || mode === 'staging' ? mode : 'development'

// Token de lecture seule, assumé public dans le bundle (cf. ops/flipt/README.md).
// Absent ⇒ Flipt désactivé, les flags rendent leur fallback.
const fliptSdkToken = import.meta.env.VITE_FLIPT_SDK_TOKEN

const fliptOptions = buildFliptClientOptions({
  clientToken: fliptSdkToken,
  environment: fliptEnvironment,
  url: fliptUrl,
})

/**
 * Persiste le snapshot du moteur d'évaluation dans localStorage à chaque
 * mise à jour du client : au prochain chargement, il sert d'état initial et
 * de fallback hors-ligne (ErrorStrategy.Fallback).
 */
const FliptSnapshotPersister = () => {
  const snapshot = useFliptSelector((client) => client?.getSnapshot())

  useEffect(() => {
    if (snapshot) {
      localStorage.setItem(FLIPT_SNAPSHOT_STORAGE_KEY, snapshot)
    }
  }, [snapshot])

  return null
}

/**
 * Branche le client Flipt (feature flags) sur toute l'app.
 *
 * Le provider est toujours rendu (les hooks du SDK throwent hors provider) :
 * sans token configuré (VITE_FLIPT_SDK_TOKEN) ou serveur injoignable sans
 * snapshot local, l'init échoue en interne et useFeatureFlag rend son
 * fallback — jamais de crash.
 */
const GozokiFliptProvider = ({ children }: { children: ReactNode }) => (
  <FliptProvider
    options={{
      ...fliptOptions,
      errorStrategy: ErrorStrategy.Fallback,
      snapshot: localStorage.getItem(FLIPT_SNAPSHOT_STORAGE_KEY) ?? undefined,
    }}
  >
    <FliptSnapshotPersister />
    {children}
  </FliptProvider>
)

export default GozokiFliptProvider
